/* =========================================================
   SecondBloom — CRO Animation Kit (JS)
   Drop this file into: Theme > Assets > cro-animations.js
   Then link it in theme.liquid, right before </body>
   (see snippets/cro-install-instructions.md)

   Vanilla JS, no dependencies, no external libraries.
   ========================================================= */
(function () {
  "use strict";

  var reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* ---------- 0. Auto-apply scroll reveal to main content sections ----------
     Automatically tags every top-level section inside <main> with "cro-reveal"
     so it fades/slides into view as the customer scrolls, without needing to
     hand-edit every section file. Header, footer, and the cart drawer are left
     alone (they live outside <main>), and anything already visible on load
     (e.g. the hero) will simply appear immediately since it's already
     intersecting the viewport when the observer runs.
  --------------------------------------------------------------- */
  function initAutoSectionReveal() {
    var main = document.querySelector("main");
    if (!main) return;
    var sections = main.querySelectorAll(":scope > .shopify-section");
    sections.forEach(function (el) {
      if (!el.classList.contains("cro-reveal") && !el.classList.contains("cro-no-reveal")) {
        el.classList.add("cro-reveal");
      }
    });
  }

  /* ---------- 1. Scroll reveal ----------
     Uses the theme's actual scrolling element as the IntersectionObserver root
     when the page uses an internally-scrolling wrapper (this theme scrolls
     .page-wrapper rather than the document) so intersection is computed against
     the real viewport instead of a locked html/body.
  ------------------------------------- */
  function getScrollRoot() {
    var wrapper = document.querySelector(".page-wrapper");
    if (wrapper) {
      var style = window.getComputedStyle(wrapper);
      if (style.overflowY === "auto" || style.overflowY === "scroll") {
        return wrapper;
      }
    }
    return null; // fall back to the browser viewport
  }

  function initScrollReveal() {
    var targets = document.querySelectorAll(".cro-reveal");
    if (!targets.length) return;

    if (reduceMotion || !("IntersectionObserver" in window)) {
      targets.forEach(function (el) { el.classList.add("is-visible"); });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { root: getScrollRoot(), threshold: 0.15 }
    );

    targets.forEach(function (el) { observer.observe(el); });
  }

  /* ---------- 2. Add to Cart button micro-interaction ----------
     Expects a button with class "cro-atc-btn" containing:
       <span class="cro-atc-label cro-atc-label-default">Add to cart</span>
       <span class="cro-atc-label cro-atc-label-success">Added</span>
     Hook this into your theme's existing add-to-cart JS/AJAX callback:
     call window.croAtcSuccess(buttonEl) when the item is confirmed added.
  --------------------------------------------------------------- */
  function croAtcSuccess(buttonEl) {
    if (!buttonEl) return;
    buttonEl.classList.add("is-success");
    bounceCartIcon();
    setTimeout(function () {
      buttonEl.classList.remove("is-success");
    }, 1600);
  }
  window.croAtcSuccess = croAtcSuccess;

  function bounceCartIcon() {
    var cartIcon = document.querySelector(
      "[data-cart-icon], .cart-icon, #cart-icon-bubble, .header__icon--cart"
    );
    if (!cartIcon) return;
    cartIcon.classList.remove("cro-cart-bounce");
    // force reflow so the animation can replay
    void cartIcon.offsetWidth;
    cartIcon.classList.add("cro-cart-bounce");
  }

  /* ---------- 3. Sticky Add to Cart bar (product pages) ---------- */
  function initStickyBar() {
    var bar = document.querySelector(".cro-sticky-bar");
    var trigger = document.querySelector("[data-cro-atc-trigger]"); // e.g. the main product form/button
    if (!bar || !trigger) return;

    if (!("IntersectionObserver" in window)) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          // Show the sticky bar once the main Add to Cart button scrolls out of view
          bar.classList.toggle("is-visible", !entry.isIntersecting);
        });
      },
      { root: getScrollRoot(), threshold: 0 }
    );
    observer.observe(trigger);
  }

  /* ---------- 4. Star rating count-up ---------- */
  function initCountUp() {
    var els = document.querySelectorAll("[data-cro-count-up]");
    els.forEach(function (el) {
      var target = parseFloat(el.getAttribute("data-cro-count-up"));
      if (isNaN(target) || reduceMotion) {
        el.textContent = target;
        return;
      }
      var duration = 800;
      var start = null;
      function step(ts) {
        if (!start) start = ts;
        var progress = Math.min((ts - start) / duration, 1);
        var value = (target * progress).toFixed(target % 1 !== 0 ? 1 : 0);
        el.textContent = value;
        if (progress < 1) requestAnimationFrame(step);
      }
      requestAnimationFrame(step);
    });
  }

  /* ---------- 5. Free shipping progress bar (cart drawer) ----------
     Set data-cro-shipping-threshold (in cents) on the fill element's
     parent, and data-cro-cart-total (in cents) — update these whenever
     your theme's cart AJAX updates the drawer.
  --------------------------------------------------------------- */
  function updateShippingBar(cartTotalCents) {
    var track = document.querySelector(".cro-shipping-bar-track");
    if (!track) return;
    var fill = track.querySelector(".cro-shipping-bar-fill");
    var threshold = parseInt(track.getAttribute("data-cro-shipping-threshold"), 10);
    if (!fill || !threshold) return;

    var pct = Math.min((cartTotalCents / threshold) * 100, 100);
    fill.style.width = pct + "%";

    var label = document.querySelector("[data-cro-shipping-label]");
    if (label) {
      var remaining = Math.max(threshold - cartTotalCents, 0) / 100;
      label.textContent =
        remaining > 0
          ? "You're $" + remaining.toFixed(2) + " away from free shipping"
          : "You've unlocked free shipping!";
    }
  }
  window.croUpdateShippingBar = updateShippingBar;

  function initAll() {
    initAutoSectionReveal();
    initScrollReveal();
    initStickyBar();
    initCountUp();
  }

  // Wait for full load (images, fonts) plus a short buffer so this theme's
  // page-load transition has finished settling layout before we measure
  // element positions for the scroll reveal / sticky bar observers.
  if (document.readyState === "complete") {
    setTimeout(initAll, 150);
  } else {
    window.addEventListener("load", function () {
      setTimeout(initAll, 150);
    });
  }
})();
